import React, { useState } from 'react';
import { Alert, Button } from '@openedx/paragon';

import { UnitCondenserGenerationResult } from '../data/api';
import copyTextToClipboard from '../utils/clipboard';

interface UnitCondenserResultProps {
  result: UnitCondenserGenerationResult;
  onReset: () => void;
  onAdjustPlan: () => void | Promise<void>;
  onRetry: () => void | Promise<void>;
}

const UnitCondenserResult: React.FC<UnitCondenserResultProps> = ({
  result,
  onReset,
  onAdjustPlan,
  onRetry,
}) => {
  const [copyConfirmationVisible, setCopyConfirmationVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const hasBlockingFindings = result.audit.blocking_findings.length > 0;
  const wordCountVariance = Math.abs(result.word_count - result.target_word_count) / result.target_word_count;
  const wordCountOutsideTolerance = wordCountVariance > result.audit.word_count_tolerance;
  const isOverTarget = result.word_count > result.target_word_count;
  const condensationSuggestions = result.audit.condensation_suggestions ?? [];
  let copyHelp = 'Self-contained HTML for an EdX Studio HTML component.';
  if (wordCountOutsideTolerance) {
    copyHelp = 'Word count is outside the target; consider reviewing the plan before publishing.';
  }
  if (hasBlockingFindings) {
    copyHelp = 'Copy requires confirmation while blocking findings remain.';
  }

  const copyHtml = async () => {
    if (hasBlockingFindings && !copyConfirmationVisible) {
      setCopyConfirmationVisible(true);
      return;
    }
    try {
      setCopyError(false);
      await copyTextToClipboard(result.html);
      setCopied(true);
      setCopyConfirmationVisible(false);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Keep the raw HTML available for manual copying if clipboard access is denied.
      setCopyConfirmationVisible(false);
      setCopyError(true);
    }
  };

  return (
    <section aria-labelledby="unit-condenser-generation-result">
      <h3 id="unit-condenser-generation-result">Review generated unit</h3>
      <div className="unit-condenser-plan-shell__metrics" aria-label="Unit word count metrics" style={{ marginBottom: '1rem' }}>
        <div className="unit-condenser-plan-shell__metric">
          <span>Original{': '}</span>
          <strong>{result.original_word_count.toLocaleString()}</strong>
          <small>{' '}words</small>
        </div>
        <div className="unit-condenser-plan-shell__metric">
          <span>Generated{': '}</span>
          <strong>{result.word_count.toLocaleString()}</strong>
          <small>{' '}words</small>
        </div>
        <div className="unit-condenser-plan-shell__metric">
          <span>Target{': '}</span>
          <strong>{result.target_word_count.toLocaleString()}</strong>
          <small>{' '}words</small>
        </div>
      </div>
      {wordCountOutsideTolerance && (
        <Alert variant="warning">
          This generated unit is outside the 5% word-count tolerance. You can copy it for review, but adjust the plan
          before publishing.
        </Alert>
      )}
      {hasBlockingFindings && (
        <Alert variant="warning">
          <strong>Review before publishing:</strong> {result.audit.blocking_findings.join(', ')}
          {copyConfirmationVisible && (
            <div className="unit-condenser-plan-shell__copy-confirmation">
              <p>
                Copying will export this result despite the findings above. Confirm only if you will resolve them
                in Studio.
              </p>
              <Button variant="warning" onClick={copyHtml}>Confirm copy</Button>
            </div>
          )}
        </Alert>
      )}
      {isOverTarget && wordCountOutsideTolerance && condensationSuggestions.length > 0 && (
        <section aria-labelledby="unit-condenser-condensation-suggestions">
          <h4 id="unit-condenser-condensation-suggestions">Ways to condense further</h4>
          <p>These suggestions are based on the evidence recorded in the current plan.</p>
          <ul>
            {condensationSuggestions.map(suggestion => (
              <li key={`${suggestion.kind}-${suggestion.concept_ids.join('-')}`}>
                <strong>{suggestion.concept_titles.join(', ')}</strong>: {suggestion.reason}
                {' '}
                <span>Potential savings: {suggestion.estimated_word_savings} words.</span>
              </li>
            ))}
          </ul>
          <div className="unit-condenser-plan-shell__actions">
            <Button variant="outline-secondary" onClick={() => { onAdjustPlan(); }}>
              Adjust plan
            </Button>
          </div>
        </section>
      )}
      {wordCountOutsideTolerance && (
        <div className="unit-condenser-plan-shell__actions">
          <Button variant="outline-secondary" onClick={() => { onRetry(); }}>
            Retry generation
          </Button>
        </div>
      )}
      <div className="unit-condenser-plan-shell__result-actions">
        <Button variant="outline-primary" onClick={copyHtml}>
          {copied ? 'Copied' : 'Copy HTML'}
        </Button>
        <span className="unit-condenser-plan-shell__copy-help">{copyHelp}</span>
      </div>
      {copyError && (
        <Alert variant="danger">
          Unable to copy generated HTML. Select it from the raw HTML section and copy it manually.
        </Alert>
      )}
      <iframe
        className="unit-condenser-plan-shell__rendered-preview"
        title="Generated unit preview"
        sandbox=""
        srcDoc={result.html}
      />
      <details className="unit-condenser-plan-shell__raw-html">
        <summary>View raw HTML</summary>
        <pre>{result.html}</pre>
      </details>
      <Button variant="outline-primary" onClick={onReset} style={{ marginTop: '.5rem' }}>Start over</Button>
    </section>
  );
};

export default UnitCondenserResult;
